import { AiOutlineLeftCircle, AiOutlineRightCircle } from "react-icons/ai";
import { TextSection } from "components/TextSection";
import { HeroImageContainer } from "components/HeroImageContainer";
import { ReactComponent as BodySVG } from "assets/content/results/body.svg";
import { ReactComponent as ImmuneVG } from "assets/content/results/immune.svg";
import { ReactComponent as CycleSVG } from "assets/content/results/cycle.svg";
import { ReactComponent as ForkAndSpoonSVG } from "assets/content/results/fork_spoon.svg";
import { ReactComponent as BackSideSVG } from "assets/content/results/back_side.svg";
import { ReactComponent as StomachSVG } from "assets/content/results/stomach.svg";
import { ReactComponent as YogaSVG } from "assets/content/results/yoga.svg";
import { ReactComponent as FaceSVG } from "assets/content/results/face.svg";
import { ReactComponent as ConsultationSVG } from "assets/svg/consultation.svg";
import { ReactComponent as TitleBodyflex } from "assets/content/title-bodyflex.svg";
import { ReactComponent as TitleSecond } from "assets/content/title-second.svg";
import { Header } from "components/Header";
import { useRef, useEffect, useState } from "react";
import { Products } from "components/Products";
import { Footer } from "components/Footer";

// Hook for scroll animations
const useScrollAnimation = (delay = 0) => {
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setIsVisible(true), delay);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -100px 0px'
      }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => observer.disconnect();
  }, [delay]);

  return { elementRef, isVisible };
};

const liAgreeStyle =
  "before:content-['✅'] before:-left-6 before:text-xl before:absolute relative";

const intro = (
  <div>
    <h1>
      Шпак Метод Бодифлекс
    </h1>
    <p>
      <strong>Шпак Метод Бодифлекс</strong> — это дыхательная тренировка, которая запускает активное жиросжигание, массирует внутренние органы и одновременно укрепляет тело.
    </p>
    <p>
      Эта практика сочетает <strong>вакуумное дыхание</strong> (диафрагмальное дыхание с задержкой) и упражнения на разные группы мышц.
    </p>

    <p>
      Результат — <strong>минус объёмы, подтянутое тело, энергия и ощущение силы внутри</strong>.
      Способствует снижению веса за счёт ускорения обмена веществ — за счёт активного преобразования жировых запасов в энергию. Помогает улучшить ясность мышления, усилить память, концентрацию внимания и способность к обучению, а также снижает раздражительность.
      И всё это — <strong>за 15 минут в день</strong>.
    </p>

    <h2>Что делает Шпак Метод Бодифлекс особенным</h2>
    <ul className="list">
      <li className={liAgreeStyle}>
        Используется <strong>самая мощная техника дыхания</strong>, которая быстро запускает обменные процессы
      </li>
      <li className={liAgreeStyle}>
        Одновременно работает тело и внутренняя система: <strong>мышцы, живот, органы малого таза, лимфа</strong>
      </li>
      <li className={liAgreeStyle}>
        Каждая тренировка — новая: <strong>методика не повторяется</strong>, а тело включается по-новому
      </li>
      <li className={liAgreeStyle}>
        Подходит как <strong>новичкам</strong>, так и тем, кто уже давно в практике
      </li>
      <li className={liAgreeStyle}>
        Укрепляется не только тело, но и привычка <strong>дышать глубоко и жить осознанно</strong>
      </li>
    </ul>

    <h2>Почему важно участвовать регулярно</h2>
    <p>
      Каждая тренировка в Шпак Методе уникальна. Мы используем разные упражнение в сочетании с дыхательными техниками, и каждый раз — это <strong>новая точка входа в тело</strong>.
    </p>
    <p>
      Метод строится как <strong>система</strong>, а не как разрозненные упражнения.
      Поэтому важно быть в потоке — тело быстро откликается, когда получает <strong>последовательную и разнообразную нагрузку</strong>.
    </p>

    <div className="bg-gradient-to-r from-rose-100 to-pink-100 p-6 rounded-lg border-l-4 border-rose-500 my-8">
      <p className="text-lg font-medium text-gray-800">
        📌 Шпак Метод Бодифлекс — это дыхательная тренировка, которая сжигает жир, возвращает силу и делает тело подтянутым. Это не фитнес. Это активация жизни через дыхание.
      </p>
    </div>
  </div>
);


const howPass = (
  <div>
    <h2>Структура тренировочной недели</h2>
    <p>
      В течение 7 дней, каждое утро в 08:00 по московскому времени, участницы получают новый видеоурок в закрытом Telegram-канале. Он включает:
    </p>
    <ol>
      <li className={liAgreeStyle}>Детальный разбор новых упражнений</li>
      <li className={liAgreeStyle}>Тренировку, актуальную на день занятия</li>
      <li className={liAgreeStyle}>Теоретический блок</li>
    </ol>
    <p>
      В первый день подробно разбирается необходимая техника дыхания.
    </p>
    <p>
      Также в программе — лекции приглашённых специалистов по теме тренировок: гинекологов, косметологов, нейропсихолога и других экспертов, делящихся практическими советами.
    </p>

    <p>
      В итоге вы получаете отработанный комплекс упражнений на каждый день, рассчитанный на месяц.
    </p>
    <p>
      Уроки доступны в записи — вы проходите их в удобное для себя время. Доступ ко всей неделе тренировок и сопутствующим материалам сохраняется на 14 дней с момента старта.
    </p>
    <p>
      При выборе тарифа с обратной связью проверка домашних заданий проводится только в течение первых 7 дней с момента начала курса — в закрытой группе WhatsApp.
    </p>

    <h3>Что нужно для занятий</h3>
    <ul>
      <li className={liAgreeStyle}>
        Коврик или большое полотенце
      </li>
      <li className={liAgreeStyle}>По желанию: антицеллюлитный крем, крем для груди, маски, патчи — как часть ухода за собой</li>
      <li className={liAgreeStyle}>
        Пространство дома или на улице, где вам комфортно
      </li>
      <li className={liAgreeStyle}>
        Зеркало или фронтальная камера — для самостоятельного контроля выполнения упражнения
      </li>
      <li className={liAgreeStyle}>
        Одежда — любая, в которой вы чувствуете себя комфортно, можно заниматься в нижнем белье или голышом.
      </li>
      <li className={liAgreeStyle}>
        Установленное приложение Telegram и стабильный интернет
      </li>
    </ul>
  </div>
);

const liExlamationStyle =
  "before:content-['❗'] before:-left-6 before:text-xl before:absolute relative";

const rulesContent = (
  <div>
    <h2>Условия участия</h2>
    <h3><strong>Шаг 1. Оплата</strong></h3>
    <p>
      Оформляется на сайте <a href="https://ilovebodyflex.online" className="text-rose-600">ilovebodyflex.online</a> через систему Robokassa. Пожалуйста, внимательно указывайте личные данные. С ошибками невозможно предоставить доступ и поддержку.
    </p>

    <h3><strong>Шаг 2. Подключение к тренировке</strong></h3>
    <p>
      После оплаты вам необходимо отправить запрос в <strong>закрытый Telegram-канал</strong>, где будут проходить все занятия. Добавление в канал занимает до 2-х дней.
    </p>
    <p>
      Вот ссылка на канал: <a href="https://t.me/+cj8A-C73ky1hZWE6" className="text-rose-600 underline">перейти в канал.</a>
    </p>
    <p>
      Если возникли вопросы или сложности — пишите в техподдержку <a href="https://t.me/shpakmetod" className="text-rose-600">@shpakmetod</a> в Telegram (ежедневно с 9:00 до 22:00 по московскому времени).
    </p>

    <h2 className="block text-rose-500">Обратите внимание</h2>
    <p>— После начала курса оплата не возвращается и не переносится ни по каким причинам</p>
    <p>— Копирование и распространение материалов запрещено и защищено законом</p>
    <p>— Контент и стиль подачи определяются автором метода. Комментарии и предложения не рассматриваются</p>
    <p>— Перед участием убедитесь в отсутствии противопоказаний, при необходимости проконсультируйтесь с врачом</p>
    <p>— Придерживаемся принципа персональной ответственности за свое здоровье.</p>
  </div>
);

const BeforeAfter = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStartX, setTouchStartX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const { elementRef, isVisible } = useScrollAnimation();

  const img1 = require("assets/content/before-after/IMG_6223.JPG");
  const img2 = require("assets/content/before-after/IMG_6224.JPG");
  const img3 = require("assets/content/before-after/IMG_6225.JPG");
  const img4 = require("assets/content/before-after/IMG_6226.JPG");
  const img5 = require("assets/content/before-after/IMG_6227.JPG");
  const img6 = require("assets/content/before-after/IMG_6228.JPG");
  const img7 = require("assets/content/before-after/IMG_6229.JPG");
  const img8 = require("assets/content/before-after/IMG_6264.JPG");
  const img9 = require("assets/content/before-after/IMG_6231.JPG");

  const images = [
    { src: img1, alt: "Результат участницы тренировки" },
    { src: img2, alt: "Результат участницы тренировки" },
    { src: img3, alt: "Результат участницы тренировки" },
    { src: img4, alt: "Результат участницы тренировки" },
    { src: img5, alt: "Результат участницы тренировки" },
    { src: img6, alt: "Результат участницы тренировки" },
    { src: img7, alt: "Результат участницы тренировки" },
    { src: img8, alt: "Результат участницы тренировки" },
    { src: img9, alt: "Результат участницы тренировки" },
  ];

  const handleLeftClick = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const handleRightClick = () => {
    if (images.length > currentSlide + 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    setTouchStartX(e.touches[0].clientX);
    setIsDragging(true);
    setDragOffset(0);
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    const currentX = e.touches[0].clientX;
    const deltaX = currentX - touchStartX;
    setDragOffset(deltaX);
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    setIsDragging(false);
    setDragOffset(0);

    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchStartX;
    const threshold = 50; // Минимальное расстояние для переключения

    if (Math.abs(deltaX) < threshold) return;

    if (deltaX > 0 && currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    } else if (deltaX < 0 && currentSlide < images.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const carouselButtonClass =
    "absolute flex top-1/2 -translate-y-1/2 items-center justify-center p-2 rounded-full border-2 z-10 border-transparent w-14 h-14 focus:outline-none focus:shadow-outline";
  const carouselButtonLeftClass =
    carouselButtonClass +
    " left-6 md:left-[18%] lg:left-[23%]" +
    (currentSlide === 0 ? " opacity-10" : "");
  const carouselButtonRightClass =
    carouselButtonClass +
    " right-6 md:right-[18%] lg:right-[23%]" +
    (currentSlide === images.length - 1 ? " opacity-10" : "");

  return (
    <div
      ref={elementRef}
      className="prose mx-auto py-8 pb-20 max-w-full"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0px)' : 'translateY(50px)',
        transition: 'all 0.8s cubic-bezier(0.25, 0.8, 0.25, 1)'
      }}
    >
      <h2
        className="text-center mb-8"
        style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0px)' : 'translateY(30px)',
          transition: 'all 0.6s cubic-bezier(0.25, 0.8, 0.25, 1)',
          transitionDelay: isVisible ? '0.2s' : '0s'
        }}
      >
        До и после Шпак Метода
      </h2>
      <div
        className="relative overflow-hidden touch-pan-y"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          cursor: isDragging ? 'grabbing' : 'grab',
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'scale(1)' : 'scale(0.95)',
          transition: 'all 0.8s cubic-bezier(0.25, 0.8, 0.25, 1)',
          transitionDelay: isVisible ? '0.4s' : '0s'
        }}
      >
        <button className={carouselButtonLeftClass} onClick={handleLeftClick}>
          <AiOutlineLeftCircle className="w-14 h-14 text-rose-500 hover:text-rose-600 transition-colors" />
        </button>
        <button className={carouselButtonRightClass} onClick={handleRightClick}>
          <AiOutlineRightCircle className="w-14 h-14 text-rose-500 hover:text-rose-600 transition-colors" />
        </button>

        <div
          className="flex"
          style={{
            transform: `translateX(${-currentSlide * 100 + (isDragging ? (dragOffset / window.innerWidth) * 100 : 0)}%)`,
            transition: isDragging ? 'none' : 'transform 0.5s cubic-bezier(0.25, 0.8, 0.25, 1)'
          }}
        >
          {images.map((image, index) => (
            <div key={index} className="w-full flex-shrink-0 flex justify-center px-4">
              <img
                src={image.src}
                alt={image.alt}
                className="w-full max-w-2xl rounded-lg shadow-lg"
              />
            </div>
          ))}
        </div>

        {/* Индикаторы слайдов с анимацией */}
        <div
          className="flex justify-center mt-8 mb-4 space-x-2"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0px)' : 'translateY(20px)',
            transition: 'all 0.6s cubic-bezier(0.25, 0.8, 0.25, 1)',
            transitionDelay: isVisible ? '0.6s' : '0s'
          }}
        >
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className="w-3 h-3 rounded-full transition-all duration-300"
              style={{
                backgroundColor: index === currentSlide ? '#f43f5e' : '#d1d5db',
                transform: index === currentSlide ? 'scale(1.2)' : 'scale(1)',
                boxShadow: index === currentSlide ? '0 0 10px rgba(244, 63, 94, 0.5)' : 'none',
                opacity: isVisible ? 1 : 0,
                transitionDelay: isVisible ? `${0.8 + index * 0.1}s` : '0s'
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const liRestrictedStyle =
  "before:content-['❌'] before:-left-6 before:text-xl before:absolute relative";

const liConsultationStyle =
  "before:content-['⚠️'] before:-left-6 before:text-xl before:absolute relative";

const contraindication = (
  <div>
    <h2>Противопоказания</h2>

    <h3 className="text-red-600 font-bold">Абсолютные:</h3>
    <ul className="list-none mb-6">
      <li className={liRestrictedStyle}>Беременность</li>
      <li className={liRestrictedStyle}>Язва желудка (в период обострения)</li>
      <li className={liRestrictedStyle}>
        Любые заболевания сердца: сердечная недостаточность, аритмия,
        тахикардия, перенесенный инфаркт, ишемическая болезнь сердца и т.п.
      </li>
      <li className={liRestrictedStyle}>Эпилепсия</li>
      <li className={liRestrictedStyle}>Диафрагмальная грыжа</li>
      <li className={liRestrictedStyle}>Травмы и грыжи позвоночника</li>
    </ul>
  </div>
);

const byAgreementWithTheDoctor = (
  <div>
    <h3 className="text-yellow-600 font-bold">Только по согласованию с врачом:</h3>
    <ul className="list-none">
      <li className={liConsultationStyle}>Гипертония 2 и 3 степени</li>
      <li className={liConsultationStyle}>Удалённая почка (нефрэктомия)</li>
      <li className={liConsultationStyle}>
        Серьёзные проблемы со зрением (высокая степень близорукости, глаукома и т. п.)
      </li>
      <li className={liConsultationStyle}>
        Любые заболевания сосудов, в том числе слабость сосудов
        (о ней могут свидетельствовать частые синяки, гематомы, «лопающиеся» сосуды, носовые кровотечения и пр.)
      </li>
      <li className={liConsultationStyle}>Киста яичника</li>
      <li className={liConsultationStyle}>Мочекаменная и желчекаменная болезнь (в период обострения)</li>
    </ul>
    <div className="mt-10">
      <ConsultationSVG className="w-full" />
    </div>
  </div>
);

const footerContent = (
  <div className="flex justify-around text-gray-400 font-serif">
    <div>
      <div>ИП Павловская Алина Сергеевна</div>
      <div>ИНН 631184627974</div>
    </div>
    <div>
      <div>Email: ilovebodyflax@gmail.com</div> <div>Договор оферты</div>
    </div>
  </div>
);

const resultIconStyle =
  "text-white text-sm flex flex-col items-center gap-4 w-52 mb-10";
const textSpanStyle = "flex justify-center text-center items-center text-sm";
const resultsContent = (
  <div className="bg-rose-500 prose min-w-full p-4 gap-x-8 text-center">
    <h1 className="text-white font-serif mt-20">
      Результаты от техники Бодифлекс:
    </h1>
    <div className="flex flex-wrap justify-around items-center mt-10">
      <div className={resultIconStyle}>
        <BodySVG />
        <div>
          <span className={textSpanStyle}>
            Помогает похудеть, уменьшить объёмы тела
          </span>
        </div>
      </div>
      <div className={resultIconStyle}>
        <ImmuneVG />
        <div>
          <span className={textSpanStyle}>
            Укрепляет иммунную систему, улучшает самочувствие, повышает
            активность
          </span>
        </div>
      </div>
      <div className={resultIconStyle}>
        <CycleSVG />
        <div>
          <span className={textSpanStyle}>Ускоряет метаболизм</span>
        </div>
      </div>
      <div className={resultIconStyle}>
        <ForkAndSpoonSVG />
        <div>
          <span className={textSpanStyle}>Уменьшает аппетит</span>
        </div>
      </div>
    </div>
    <div className="flex flex-wrap justify-around items-center">
      <div className={resultIconStyle}>
        <BackSideSVG />
        <div>
          <span className={textSpanStyle}>Избавляет от отеков и целлюлита</span>
        </div>
      </div>
      <div className={resultIconStyle}>
        <FaceSVG />
        <div>
          <span className={textSpanStyle}>Улучшает цвет и тонус кожи</span>
        </div>
      </div>
      <div className={resultIconStyle}>
        <StomachSVG />
        <div>
          <span className={textSpanStyle}>Нормализует работу кишечника</span>
        </div>
      </div>
      <div className={resultIconStyle}>
        <YogaSVG />
        <div>
          <span className={textSpanStyle}>Очищает лимфатическую систему</span>
        </div>
      </div>
    </div>
  </div>
);

const Main = ({ headerImageLeft, scrollTo }: any) => {
  return (
    <>
      <div className="bg-red-100 relative lg:hidden">
        <div className="sm:flex relative h-screen min-h-[600px] clip-hero-bg-white bg-white">
          <div className="absolute flex bg-heroBgMobile bg-cover bg-center bg-no-repeat w-full h-full clip-hero-bg-mobile"></div>
        </div>
        <div className="flex justify-center items-center w-full">
          <div className="absolute font-ristretto flex flex-col justify-center items-center content-center w-full bottom-12">
            <div className="text-red-500 font-semibold text-5xl sm:text-7xl md:text-8xl leading-none">
              #ШпакМетод
            </div>
            <div className="text-cyan-600 font-semibold text-4xl sm:text-6xl md:text-8xl leading-none">
              Бодифлекс
            </div>
            <div className="text-gray-700 mt-4 sm:mt-6 text-center max-w-lg px-4" style={{
              fontSize: '1.2rem',
              fontWeight: '500'
            }}>
              Уменьшает отеки и объемы, сжигает жиры, выводит лишнюю жидкость
            </div>
            <div>
              <div onClick={() => scrollTo("buyRef")}
                className="mt-4 sm:mt-6 rounded-md bg-red-500 p-3 px-8 sm:p-4 sm:px-16 uppercase text-white cursor-pointer hover:bg-red-600 transform hover:scale-105 transition-all duration-300 shadow-lg"
                style={{
                  fontSize: '1.2rem',
                  fontWeight: '600'
                }}
              >
                Купить
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="hidden h-screen min-h-[600px] w-auto bg-rose-200 md:hidden lg:visible lg:flex">
        <div className="flex bg-white w-2/5 bg-cover bg-center bg-no-repeat clip-header-white relative">
          <div className="asbolute bg-alinaLay bg-cover bg-center bg-no-repeat flex bg-red-100 clip-header z-10 w-full"></div>
        </div>
        <div
          className="mx-auto flex w-1/2 flex-wrap content-center items-center justify-center"
          ref={headerImageLeft}
        >
          <div className="mr-4 flex flex-grow-0 flex-col font-ristretto">
            <div className="flex flex-col">
              <div className="text-red-500 font-semibold text-8xl leading-none">
                #ШпакМетод
              </div>
              <div className="text-cyan-600 font-semibold text-8xl leading-none">
                Бодифлекс
              </div>
            </div>
            <div className="text-gray-700 mt-6 max-w-lg" style={{
              fontSize: '1.8rem',
              fontWeight: '500'
            }}>
              Уменьшает отеки и объемы, сжигает жиры, выводит лишнюю жидкость
            </div>
            <div>
              <div onClick={() => scrollTo("buyRef")}
                className="mt-8 inline-block rounded-md bg-red-500 py-4 px-20 uppercase text-white cursor-pointer hover:bg-red-600 transform hover:scale-105 transition-all duration-300 shadow-lg"
                style={{
                  fontSize: '1.4rem',
                  fontWeight: '600'
                }}
              >
                Купить
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const products = [
  {
    id: 10,
    name: "Стандарт",
    description: (
      <ul className="list-none pl-4">
        <li className="font-bold">
          — Доступ к информации и тренировкам в закрытом Telegram-канале на 14 дней
        </li>
        <li className="font-bold">
          — Без обратной связи и проверки домашних заданий
        </li>
        <li className="text-sm mt-2">
          <strong>Важно:</strong> Вам необходимо самостоятельно отправить запрос на добавление в группу и внимательно указать свои данные в анкете.
        </li>
        <li className="text-sm mt-2">
          <a href="https://t.me/+cj8A-C73ky1hZWE6" className="text-rose-600 underline">Перейти в группу.</a>
        </li>
      </ul>
    ),
    price: "10 000 ₽",
  },
  {
    id: 11,
    name: "Стандарт+",
    description: (
      <ul className="list-none pl-4">
        <li className="font-bold">
          — Доступ к информации и тренировкам в закрытом Telegram-канале на 14 дней
        </li>
        <li className="font-bold">
          — Проверка только техники дыхания в общем чате WhatsApp — в течение первых 72 часов после старта курса
        </li>
        <li className="text-sm mt-2">
          <strong>Важно:</strong> Вам необходимо самостоятельно отправить запрос на добавление в группу и внимательно указать свои данные в анкете.
        </li>
        <li className="text-sm mt-2">
          <a href="https://t.me/+cj8A-C73ky1hZWE6" className="text-rose-600 underline">Перейти в группу.</a>
        </li>
      </ul>
    ),
    price: "12 000 ₽",
  },
  {
    id: 12,
    name: "VIP",
    description: (
      <div>
        <p className="mb-3 text-sm pl-4">
          Если ты хочешь быть уверена, что всё делаешь правильно, не согласна на "как получится", и по-настоящему стремишься к результату — этот тариф для тебя.
        </p>
        <ul className="list-none space-y-1">
          <li className="font-bold">
            — Доступ к информации и тренировкам в закрытом Telegram-канале на 14 дней
          </li>
          <li className="font-bold">
            — Проверка техники дыхания и всех упражнений в общем VIP-чате WhatsApp
          </li>
          <li className="font-bold">
            — Жёсткий режим и тотальный контроль — чтобы не осталось ни шанса «отмазаться»
          </li>
        </ul>
        <div className="text-sm mt-3 pt-2 border-t border-gray-200 pl-4">
          <strong>Важно:</strong> Проверка домашних заданий проводится только в течение первых 7 дней с момента начала курса. Проверка осуществляется в закрытой группе WhatsApp. Запрос в группу вы отправляете самостоятельно, внимательно указывая данные в анкете.
        </div>
        <div className="text-sm mt-2 pl-4">
          <a href="https://t.me/+cj8A-C73ky1hZWE6" className="text-rose-600 underline">Перейти в группу.</a>
        </div>
      </div>
    ),
    price: "15 000 ₽",
  },
  {
    id: 35,
    name: "VIP+",
    description: (
      <div>
        <p className="mb-3 text-sm pl-4">
          Если ты хочешь личного контроля от Алины Шпак — этот тариф для тебя.
        </p>
        <ul className="list-none space-y-1">
          <li className="font-bold">
            — Доступ к информации и тренировкам в закрытом Telegram-канале на 14 дней
          </li>
          <li className="font-bold">
            — Проверка техники дыхания и всех упражнений лично Алиной Шпак в WhatsApp
          </li>
          <li className="font-bold">
            — Жёсткий режим и тотальный контроль — чтобы не осталось ни малейшего шанса «отмазаться»
          </li>
        </ul>
        <div className="text-sm mt-3 pt-2 border-t border-gray-200 pl-4">
          <strong>Важно:</strong> Проверка домашних заданий проводится только в течение первых 7 дней с момента начала курса. Проверка осуществляется в закрытой группе WhatsApp. Запрос в группу вы отправляете самостоятельно, внимательно указывая данные в анкете.
        </div>
        <div className="text-sm mt-2 pl-4">
          <a href="https://t.me/+cj8A-C73ky1hZWE6" className="text-rose-600 underline">Перейти в группу.</a>
        </div>
      </div>
    ),
    price: "100 000 ₽",
  },
];

export const Bodyflex = () => {
  const headerRef = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLDivElement>(null);
  const introRef = useRef<HTMLDivElement>(null);
  const howRef = useRef<HTMLDivElement>(null);
  const rulesRef = useRef<HTMLDivElement>(null);
  const reviewsRef = useRef<HTMLDivElement>(null);
  const contraindicationRef = useRef<HTMLDivElement>(null);
  const buyRef = useRef<HTMLDivElement>(null);

  const headerImageLeft = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const hash = window.location.hash.substring(1);
    if (hash === "buyRef" && buyRef.current) {
      setTimeout(() => {
        window.scrollTo(0, buyRef.current?.offsetTop || 0);
      }, 100);
    }
  }, []);

  const scrollTo = (name: string) => {
    if (name === "top") window.scrollTo(0, introRef?.current?.offsetTop || 0);
    if (name === "intro") window.scrollTo(0, introRef?.current?.offsetTop || 0);
    if (name === "how") window?.scrollTo(0, howRef?.current?.offsetTop || 0);
    if (name === "rules")
      window?.scrollTo(0, rulesRef?.current?.offsetTop || 0);
    if (name === "testimonials")
      window?.scrollTo(0, reviewsRef?.current?.offsetTop || 0);
    if (name === "contraindication")
      window?.scrollTo(0, contraindicationRef?.current?.offsetTop || 0);
    if (name === "buyRef") window?.scrollTo(0, buyRef?.current?.offsetTop || 0);
  };

  return (
    <div ref={topRef}>
      <Header scrollTo={scrollTo} ref={headerRef} />
      <Main headerImageLeft={headerImageLeft} scrollTo={scrollTo} />
      <div ref={introRef} />
      <TextSection content={intro} />
      {resultsContent}
      <div ref={howRef} />
      <HeroImageContainer imageName="how-pass.png" />
      <TextSection content={howPass} />
      <div ref={rulesRef} />
      <HeroImageContainer imageName="rules.png" />
      <TextSection content={rulesContent} />
      <div ref={reviewsRef} />
      <div className="bg-separator h-16 bg-repeat-x" />
      <BeforeAfter />
      <div className="bg-separator h-16 bg-repeat-x" />
      <div ref={contraindicationRef} />
      <TextSection content={contraindication} />
      <TextSection
        className="bg-gray-100 pt-4"
        content={byAgreementWithTheDoctor}
      />
      <div ref={buyRef} />
      <Products date={"15 мая"} products={products} closed={true} closedTitle="Набор закрыт. Ожидайте следующий" />
      <Footer />
      {/* <div className="prose mx-auto p-8">{footerContent}</div> */}
    </div>
  );
};
